import React, { useContext, useEffect } from 'react';

import AuthContext from 'app/src/contexts/AuthContext';
import Input, { Field, FieldLabel } from 'app/src/elements/inputs';
import AccountInput from 'app/src/elements/AccountInput';
import AssetInput from 'app/src/elements/AssetInput';
import EntityNameInput from 'app/src/elements/EntityNameInput';
import { Text } from 'app/src/styles';

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const nameToLabel = name => (
  name.match(/^[a-z]+|[A-Z][a-z]*/g).map(word => (
    word[0].toUpperCase() + word.substr(1).toLowerCase()
  )).join(' ')
);

export const defaultValues = ({ fields, nestedFields, resource }) => {
  let values = {};

  if (resource) {
    values.id = resource.id;
  }

  Object.keys(fields).forEach(name => {
    if (resource?.[name]) {
      values[name] = resource[name];
    } else if ('boolean' === fields[name].type) {
      values[name] = (resource || {})[name];
    } else if (name.endsWith('Id')) {
      const associationName = name.substring(0, name.length - 2);
      values[name] = (resource || {})[associationName]?.id || '';
    } else {
      values[name] = (resource || {})[name] || '';
    }

    if (fields[name].extraLabels) {
      values = {
        ...defaultValues({ fields: fields[name].extraLabels, resource }),
        ...values,
      };
    }
  });

  nestedFields?.forEach(nested => {
    nested.nestedResources.forEach((nestedResource, index) => {
      const nestedValues = defaultValues({
        fields: nested.fields,
        resource: nestedResource,
      });

      Object.keys(nestedValues).forEach(nestedName => {
        values[`${nested.prefix}.${index}.${nestedName}`] =
          nestedValues[nestedName];
      });
    });
  });

  return values;
};

export const prepareInput = (input, fields) => {
  Object.keys(fields).forEach(name => {
    const field = fields[name];

    if ('int' === field.type?.toLowerCase()) {
      input[name] = '' === input[name] ? null : parseInt(input[name]);
    }

    if ('float' === field.type?.toLowerCase()) {
      input[name] = '' === input[name] ? null : parseFloat(input[name]);
    }

    if ('null' === field.blank && !input[name]) {
      input[name] = null
    }

    if ('boolean' === field.type) {
      input[name] = !!input[name];
    }
  });

  return input;
};

const DEFAULT_OPTION = { value: '', label: 'Select' };

// Separate component to allow simplify using useEffect when
// oneOption === 'no-choice'
const Select = ({ name, control, errors, field, setValue, inline, width }) => {
  const client = useContext(AuthContext);

  let options = field.select;

  if (field.clientSelect) {
    options = [DEFAULT_OPTION, ...(client[field.clientSelect] || [])];
  } else if (0 === options.length) {
    options = [];
  } else if (!options[0].value && !options[0].label) {
    options = [
      ...options.map(option => ({ value: option, label: option }))
    ];
  }

  if (field.addDefault) {
    options = [DEFAULT_OPTION, ...options];
  }

  useEffect(() => {
    if ('no-choice' === field.oneOption && setValue) {
      if (2 === options.length) {
        setValue(name, client[field.clientSelect][0].value);
      }
    }
  }, []);

  if ('no-choice' === field.oneOption && setValue) {
    if (2 === options.length) {
      return (
        <Field inline={inline} errors={errors?.[name]} width={width}>
          {!field.inline && (
            <FieldLabel
              name={name}
              label={field.label || nameToLabel(name)}
              errors={errors}
            />
          )}
          <Text style={{fontWeight: 'bold'}}>{options[1].label}</Text>
        </Field>
      );
    }
  }

  return (
    <Input.Picker
      label={field.label || nameToLabel(name)}
      name={name}
      key={name}
      hint={field.hint}
      rules={field.rules}
      options={options || []}
      control={control}
      errors={errors}
      buttons={field.buttons}
      inline={inline}
      width={width}
    />
  );
};

export const Fields = ({ fields, control, errors, setValue, inline }) => {
  return (
    Object.keys(fields).map((name, fieldIndex) => {
      const field = fields[name];

      if (field.disabled) {
        return null;
      }

      const generalInputProps = {
        name,
        key: name,
        label: (field.label || nameToLabel(name)),
        hint: field.hint,
        rules: field.rules,
        control,
        errors,
        inline,
        width: field.width,
        setValue,
      };

      if (field.select || field.clientSelect) {
        return (
          <Select
            name={name}
            key={name}
            control={control}
            errors={errors}
            field={field}
            setValue={setValue}
            inline={inline}
            width={field.width}
          />
        );
      } else if ('boolean' === field.type) {
        return (
          <Input.Switch
            label={field.label || nameToLabel(name)}
            name={name}
            key={name}
            control={control}
            inline={inline}
            width={field.width}
          />
        );
      } else if ('date' === field.type) {
        return (
          <Input.Date
            label={field.label || nameToLabel(name)}
            name={name}
            key={name}
            hint={field.hint}
            rules={field.rules}
            control={control}
            errors={errors}
            inline={inline}
            width={field.width}
          />
        );
      } else if ('account' === field.type) {
        return (
          <AccountInput
            {...generalInputProps}
            preferredTypes={field.preferredTypes}
            preferredOnly={field.preferredOnly}
          />
        );
      } else if ('asset' === field.type) {
        return (
          <AssetInput
            {...generalInputProps}
            extraLabels={field.extraLabels}
            txn={field.txn}
            onFeeCreated={field.onFeeCreated}
          />
        );
      } else if ('entityName' === field.type) {
        return (
          <EntityNameInput
            {...generalInputProps}
            preferredTypes={field.preferredTypes}
          />
        );
      } else {
        return (
          <Input.Text
            label={field.label || nameToLabel(name)}
            name={name}
            key={name}
            hint={field.hint}
            rules={field.rules}
            control={control}
            errors={errors}
            inputType={field.type?.toLowerCase()}
            inline={inline}
            width={field.width}
            {...field.inputProps}
          />
        );
      }
    })
  );
};
